import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { applyTheme } from "../utils/theme";
import Sidebar from "../components/common/Sidebar";
import Navbar from "../components/common/Navbar";
import DataTable from "../components/common/DataTable";
import { FaSearch, FaChevronDown } from "react-icons/fa";
import SideModal from "../components/common/SideModal";
import { useApi } from "../hooks/useApi";
import { message } from "antd";

const UserManagement = () => {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const [updateTriesModalOpen, setUpdateTriesModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOption, setSearchOption] = useState("name");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [updateTriesValue, setUpdateTriesValue] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const { getUsers, updateUserTries, deactivateUser, activateUser } = useApi();
  const mode = useSelector((state) => state.theme.mode);

  // Define columns inside component to access translation function
  const columns = [
    {
      key: "fullName",
      title: t("table.name"),
      render: (val) => (
        <span className="text-text-tablebody font-semibold">{val}</span>
      ),
    },
    {
      key: "email",
      title: t("table.email"),
      render: (val) => <span className="text-text-tablebody">{val}</span>,
    },
    {
      key: "isActive",
      title: t("table.status"),
      render: (val) => (
        <span
          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
            val === true
              ? "bg-[#55CE631A] text-[#55CE63]"
              : val === false
              ? "bg-[#FFBC341A] text-[#FFBC34]"
              : "bg-gray-200 text-gray-600"
          }`}
        >
          {val === true ? "Active" : val === false ? "In Active" : "Unknown"}
        </span>
      ),
    },
    {
      key: "tries",
      title: t("table.freeTriesLeft"),
      render: (val) => <span className="text-text-tablebody">{val || 0}</span>,
    },
    {
      key: "action",
      title: t("table.action"),
    },
  ];

  useEffect(() => {
    applyTheme(mode);
  }, [mode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch users on component mount and when pagination changes
  //   useEffect(() => {
  //     const fetchUsers = async () => {
  //     try {
  //         const response = await getUsers(pagination.currentPage, pagination.itemsPerPage);

  //         console.log('Users API response:', response);
  //         console.log('Response.data:', response.data);
  //         console.log('Response.data type:', typeof response.data);
  //         console.log('Is response.data array?', Array.isArray(response.data));

  //         if (response.success) {
  //         // Handle the actual API response structure
  //         let usersData = [];
  //         if (response.data && response.data.docs && Array.isArray(response.data.docs)) {
  //             usersData = response.data.docs;
  //         } else if (Array.isArray(response.data)) {
  //             usersData = response.data;
  //         } else if (Array.isArray(response.result)) {
  //             usersData = response.result;
  //         } else {
  //             console.warn('No valid users array found in response:', response);
  //             usersData = [];
  //         }

  //         console.log('Setting users to:', usersData);
  //         setUsers(usersData);
  //         setPagination(prev => ({
  //             ...prev,
  //             totalPages: response.data?.totalPages || 1,
  //             totalItems: response.data?.totalDocs || 0,
  //         }));
  //         } else {
  //         message.error(response.message || "Failed to fetch users");
  //         }
  //     } catch (error) {
  //         message.error("Error fetching users");
  //         console.error("Error fetching users:", error);
  //     }
  //     };

  //     fetchUsers();
  // }, [pagination.currentPage, pagination.itemsPerPage, getUsers]);

  // Fetch users on component mount and when pagination changes
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Build search parameters based on search option and term
        const searchParams = {};
        if (searchTerm.trim()) {
          switch (searchOption) {
            case "name":
              searchParams.name = searchTerm.trim();
              break;
            case "email":
              searchParams.email = searchTerm.trim();
              break;
            default:
              break;
          }
        }

        const response = await getUsers(
          pagination.currentPage,
          pagination.itemsPerPage,
          searchParams
        );

        console.log("Users API response:", response);
        console.log("Response.data:", response.data);
        console.log("Response.data type:", typeof response.data);
        console.log("Is response.data array?", Array.isArray(response.data));

        if (response.success) {
          // Handle the actual API response structure
          let usersData = [];
          if (
            response.data &&
            response.data.users &&
            Array.isArray(response.data.users)
          ) {
            usersData = response.data.users;
          } else if (
            response.data &&
            response.data.docs &&
            Array.isArray(response.data.docs)
          ) {
            usersData = response.data.docs;
          } else if (Array.isArray(response.data)) {
            usersData = response.data;
          } else if (Array.isArray(response.result)) {
            usersData = response.result;
          } else {
            console.warn("No valid users array found in response:", response);
            usersData = [];
          }

          console.log("Setting users to:", usersData);
          setUsers(usersData);
          setPagination((prev) => ({
            ...prev,
            totalPages: response.data?.pagination?.totalPages || 1,
            totalItems: response.data?.pagination?.totalDocs || 0,
          }));
        } else {
          message.error(response.message || "Failed to fetch users");
        }
      } catch (error) {
        message.error("Error fetching users");
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.currentPage,
    pagination.itemsPerPage,
    searchOption,
    searchTrigger,
    getUsers,
  ]);

  // Handler for opening update tries modal
  const handleUpdateTries = (row) => {
    setSelectedUser(row);
    setUpdateTriesValue(row.tries?.toString() || "0");
    setUpdateTriesModalOpen(true);
  };

  // Handler for updating user tries
  //   const handleSaveUpdateTries = async () => {
  //     try {
  //       const response = await updateUserTries(selectedUser._id, parseInt(updateTriesValue));

  //       if (response.success) {
  //         message.success("User tries updated successfully!");

  //         // Update the specific user in the users array
  //         setUsers(prevUsers =>
  //           prevUsers.map(user =>
  //             user._id === selectedUser._id
  //               ? { ...user, freeTriesLeft: parseInt(updateTriesValue) }
  //               : user
  //           )
  //         );

  //         setUpdateTriesModalOpen(false);
  //         setSelectedUser(null);
  //         setUpdateTriesValue("");
  //       } else {
  //         message.error(response.message || "Failed to update user tries");
  //       }
  //     } catch (error) {
  //       message.error("Error updating user tries");
  //       console.error("Error updating user tries:", error);
  //     }
  //   };
  const handleSaveUpdateTries = async () => {
    try {
      const response = await updateUserTries(
        selectedUser._id,
        parseInt(updateTriesValue)
      );

      if (response.success) {
        message.success("User tries updated successfully!");

        // Update the specific user in the users array
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === selectedUser._id
              ? { ...user, tries: parseInt(updateTriesValue) }
              : user
          )
        );

        setUpdateTriesModalOpen(false);
        setSelectedUser(null);
        setUpdateTriesValue("");
      } else {
        message.error(response.message || "Failed to update user tries");
      }
    } catch (error) {
      message.error("Error updating user tries");
      console.error("Error updating user tries:", error);
    }
  };

  // Handler for deactivating a user
  //   const handleDeactivate = async (userId) => {
  //     try {
  //       const response = await deactivateUser(userId);

  //       if (response.success) {
  //         message.success("User deactivated successfully!");

  //         // Update the specific user in the users array
  //         setUsers(prevUsers =>
  //           prevUsers.map(user =>
  //             user._id === userId
  //               ? { ...user, status: "In Active" }
  //               : user
  //           )
  //         );

  //         // If the modal is open and we're deactivating the selected user, update it too
  //         if (selectedUser && selectedUser._id === userId) {
  //           setSelectedUser(prev => ({ ...prev, status: "In Active" }));
  //         }
  //       } else {
  //         message.error(response.message || "Failed to deactivate user");
  //       }
  //     } catch (error) {
  //       message.error("Error deactivating user");
  //       console.error("Error deactivating user:", error);
  //     }
  //   };

  const handleDeactivate = async (userId) => {
    try {
      const response = await deactivateUser(userId);

      if (response.success) {
        message.success("User deactivated successfully!");

        // Update the specific user in the users array
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === userId ? { ...user, isActive: false } : user
          )
        );

        // If the modal is open and we're deactivating the selected user, update it too
        if (selectedUser && selectedUser._id === userId) {
          setSelectedUser((prev) => ({ ...prev, isActive: false }));
        }
      } else {
        message.error(response.message || "Failed to deactivate user");
      }
    } catch (error) {
      message.error("Error deactivating user");
      console.error("Error deactivating user:", error);
    }
  };

  // Handler for activating a user
  const handleActivate = async (userId) => {
    try {
      const response = await activateUser(userId);

      if (response.success) {
        message.success("User activated successfully!");

        // Update the specific user in the users array
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === userId ? { ...user, isActive: true } : user
          )
        );

        // If the modal is open and we're activating the selected user, update it too
        if (selectedUser && selectedUser._id === userId) {
          setSelectedUser((prev) => ({ ...prev, isActive: true }));
        }
      } else {
        message.error(response.message || "Failed to activate user");
      }
    } catch (error) {
      message.error("Error activating user");
      console.error("Error activating user:", error);
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  const handleSearchInputChange = (value) => {
    setSearchTerm(value);
    // Don't trigger search on input change, only update the state
  };

  const handleSearch = () => {
    // Reset to first page when searching
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    // Trigger search by updating searchTrigger
    setSearchTrigger((prev) => prev + 1);
  };

  const handleSearchOptionChange = (option) => {
    setSearchOption(option);
    setDropdownOpen(false);
    setSearchTerm(""); // Clear search when changing option
  };

  const getSearchOptionLabel = (option) => {
    switch (option) {
      case "name":
        return t("search.byName");
      case "email":
        return t("search.byEmail");
      default:
        return t("search.byName");
    }
  };

  // No need for client-side filtering since we're doing server-side filtering
  const filteredUsers = users;

  // Columns with updated action buttons
  const columnsWithActions = columns.map((col) => {
    if (col.key === "action") {
      return {
        ...col,
        render: (_, row) => (
          <div className="flex justify-center gap-4 items-center">
            <button
              className="text-text-tableactionbtn hover:underline"
              onClick={() => handleUpdateTries(row)}
            >
              {t("table.updateTries")}
            </button>
            {row.isActive ? (
              <button
                className="text-[#FF0000] hover:underline"
                onClick={() => handleDeactivate(row._id)}
              >
                {t("table.deactivate")}
              </button>
            ) : (
              <button
                className="text-[#00B050] hover:underline"
                onClick={() => handleActivate(row._id)}
              >
                {t("table.activate")}
              </button>
            )}
          </div>
        ),
      };
    }
    return col;
  });

  return (
    <div className="min-h-screen bg-background-main">
      <Navbar
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen}
      />
      <div className="flex flex-col lg:flex-row">
        <Sidebar
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
        <main className="flex-1 p-4 lg:p-6 xl:p-8 pt-4 w-full overflow-x-auto">
          {/* Header and Filters */}
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-0 justify-start mb-4">
            <div className="flex items-center bg-background-search rounded-full sm:rounded-l-full sm:rounded-r-none px-4 py-2 w-full sm:max-w-sm border border-border-search">
              <FaSearch
                className="text-[#81879D] mr-2 cursor-pointer hover:text-accent transition-colors"
                onClick={handleSearch}
              />
              <input
                type="text"
                placeholder={(() => {
                  switch (searchOption) {
                    case "name":
                      return t("search.searchName");
                    case "email":
                      return t("search.searchEmail");
                    default:
                      return t("search.searchName");
                  }
                })()}
                value={searchTerm}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                className="bg-transparent outline-none flex-1 text-input-text placeholder-[#81879D]"
              />
            </div>
            <div
              className="relative flex items-center bg-background-search rounded-full sm:rounded-r-full sm:rounded-l-none px-4 py-2 w-full sm:w-auto sm:min-w-[120px] border-l border-l-border-search sm:border-l-searchbar-divider border-r border-r-border-search border-y border-y-border-search cursor-pointer"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <span className="text-[#A9AEBC] mr-2">
                {getSearchOptionLabel(searchOption)}
              </span>
              <FaChevronDown
                className={`text-[#81879D] ml-auto transition-transform ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              />

              {/* Dropdown Options */}
              {dropdownOpen && (
                <div className="absolute top-full left-0 right-0 bg-background-search text-[#A9AEBC] rounded-b-lg shadow-lg z-10 mt-1">
                  <div
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => handleSearchOptionChange("name")}
                  >
                    {t("search.byName")}
                  </div>
                  <div
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => handleSearchOptionChange("email")}
                  >
                    {t("search.byEmail")}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-background-card rounded-b-lg shadow-md">
            <DataTable columns={columnsWithActions} data={filteredUsers} />
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 text-text-primary text-sm">
            <span>
              {t("pagination.showing")}{" "}
              {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}-
              {Math.min(
                pagination.currentPage * pagination.itemsPerPage,
                pagination.totalItems
              )}{" "}
              {t("pagination.of")} {pagination.totalItems}
            </span>
            <div className="flex items-center gap-1">
              <button
                className={`px-3 py-1 rounded ${
                  pagination.currentPage === 1
                    ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                    : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                }`}
                disabled={pagination.currentPage === 1}
                onClick={() => handlePageChange(1)}
              >
                {t("pagination.first")}
              </button>
              <button
                className={`px-3 py-1 rounded ${
                  pagination.currentPage === 1
                    ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                    : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                }`}
                disabled={pagination.currentPage === 1}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                {t("pagination.back")}
              </button>

              {/* Page numbers - different display for mobile vs desktop */}
              {/* Mobile: Show only page 1 and last page */}
              <div className="md:hidden flex items-center gap-1">
                <button
                  className={`px-3 py-1 rounded ${
                    pagination.currentPage === 1
                      ? "bg-accent text-white font-bold"
                      : "bg-background-card border border-border-paginationbtn hover:bg-accent hover:text-white"
                  }`}
                  onClick={() => handlePageChange(1)}
                >
                  1
                </button>
                {pagination.totalPages > 1 && (
                  <>
                    <span className="px-2">...</span>
                    <button
                      className={`px-3 py-1 rounded ${
                        pagination.currentPage === pagination.totalPages
                          ? "bg-accent text-white font-bold"
                          : "bg-background-card border border-border-paginationbtn hover:bg-accent hover:text-white"
                      }`}
                      onClick={() => handlePageChange(pagination.totalPages)}
                    >
                      {pagination.totalPages}
                    </button>
                  </>
                )}
              </div>

              {/* Desktop: Show full pagination */}
              <div className="hidden md:flex items-center gap-1">
                {Array.from(
                  { length: Math.min(5, pagination.totalPages) },
                  (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        className={`px-3 py-1 rounded ${
                          pagination.currentPage === pageNum
                            ? "bg-accent text-white font-bold"
                            : "bg-background-card border border-border-paginationbtn hover:bg-accent hover:text-white"
                        }`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}

                {pagination.totalPages > 5 && (
                  <>
                    <span className="px-2">...</span>
                    <button
                      className="px-3 py-1 rounded bg-background-card border border-border-paginationbtn hover:bg-accent hover:text-white"
                      onClick={() => handlePageChange(pagination.totalPages)}
                    >
                      {pagination.totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                className={`px-3 py-1 rounded ${
                  pagination.currentPage === pagination.totalPages
                    ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                    : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                }`}
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
              >
                {t("pagination.next")}
              </button>
              <button
                className={`px-3 py-1 rounded ${
                  pagination.currentPage === pagination.totalPages
                    ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                    : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                }`}
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => handlePageChange(pagination.totalPages)}
              >
                {t("pagination.last")}
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Update Tries Modal */}
      <SideModal
        open={updateTriesModalOpen}
        onClose={() => {
          setUpdateTriesModalOpen(false);
          setSelectedUser(null);
          setUpdateTriesValue("");
        }}
        title="Update Tries"
        actions={
          <div className="flex gap-3 w-full">
            <button
              className="flex-1 py-3 rounded-xl border border-accent text-accent font-semibold text-lg bg-transparent"
              onClick={() => {
                setUpdateTriesModalOpen(false);
                setSelectedUser(null);
                setUpdateTriesValue("");
              }}
            >
              Cancel
            </button>
            <button
              className="flex-1 py-3 rounded-xl bg-accent text-white font-semibold text-lg"
              onClick={handleSaveUpdateTries}
            >
              Save
            </button>
          </div>
        }
      >
        {selectedUser && (
          <div>
            <div className="mb-4">
              <div className="font-medium text-text-primary mb-2">
                Remaining Tries : {selectedUser.tries || 0}
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Update Tries
              </label>
              <input
                type="number"
                value={updateTriesValue}
                onChange={(e) => setUpdateTriesValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Enter new tries value"
              />
            </div>
          </div>
        )}
      </SideModal>
    </div>
  );
};

export default UserManagement;
